// noinspection JSUnusedGlobalSymbols
import {experimental_extendTheme as extendMuiTheme, SupportedColorScheme} from "@mui/material/styles";
import {
    autocompleteClasses,
    buttonClasses,
    formHelperTextClasses,
    inputBaseClasses,
    menuItemClasses,
    responsiveFontSizes,
    selectClasses,
    switchClasses,
    tabClasses,
    toggleButtonClasses
} from "@mui/material";
import {Palette} from "@mui/material/styles/createPalette";
import DateUtils from "../../core/services/date-utils";
import UIUtils from "../utils";
import Utils from "../../core/services/utils/utils";
import DefaultColorTheme, {MaskedColorTheme} from "./default-color-theme";
import EnvService from "../../core/services/env";


/**
 * The Material UI theme of the application.
 *
 * @param colorTheme The color theme of the application.
 */
const muiTheme = (colorTheme: Record<SupportedColorScheme, { palette: Palette }>) => {
    return extendMuiTheme({
        cssVarPrefix: UIUtils.ThemePrefix.default,
        breakpoints: {
            keys: ['xs', 'sm', 'md', 'mdlg', 'lg', 'lgxl', 'xl', 'xxl'],
            values: {
                xs: 0,
                sm: 600,
                smMd: 750,
                md: 900,
                mdlg: 1050,
                lg: 1200,
                lgxl: 1368,
                xl: 1536,
                xxl: 1920,
            },
        },
        colorSchemes: {
            light: {
                palette: {
                    primary: {
                        main: colorTheme.light.palette.primary.main,
                        light: colorTheme.light.palette.primary.light,
                    },
                    secondary: {
                        lighter: colorTheme.light.palette.secondary.lighter,
                        light: colorTheme.light.palette.secondary.light,
                        main: colorTheme.light.palette.secondary.main,
                    },
                    tertiary: {
                        light: colorTheme.light.palette.tertiary.light,
                        main: colorTheme.light.palette.tertiary.main,
                    },
                    background: {
                        paper: colorTheme?.light.palette.background.paper,
                        default: colorTheme?.light.palette.background.default,
                        secondary: colorTheme?.light.palette.background.secondary,
                        tertiary: colorTheme?.light.palette.background.tertiary,
                    },
                    warning: {
                        light: colorTheme.light.palette.warning.light,
                        main: colorTheme.light.palette.warning.main,
                    },
                    error: {
                        light: colorTheme.light.palette.error.light,
                        main: colorTheme.light.palette.error.main,
                    },
                    success: {
                        light: colorTheme.light.palette.success.light,
                        main: colorTheme.light.palette.success.main,
                        dark: colorTheme.light.palette.success.dark,
                    },
                    common: {
                        white: colorTheme.light.palette.common.white,
                        black: colorTheme.light.palette.common.black,
                    },
                    divider: colorTheme.light.palette.divider,
                    text: {
                        primary: colorTheme.light.palette.text.primary,
                        secondary: colorTheme.light.palette.text.secondary,
                        tertiary: colorTheme.light.palette.text.tertiary,
                        disabled: colorTheme.light.palette.text.disabled,
                    },
                    'app-specific': {
                        shadow: 'rgba(0,0,0,0.2)',
                        'card-border-color': colorTheme.light.palette['app-specific']["card-border-color"],
                        'scroll-bar': {
                            track: colorTheme.light.palette['app-specific']['scroll-bar'].track,
                            thumb: colorTheme.light.palette['app-specific']['scroll-bar'].thumb,
                        },
                        'chart': {
                            "1": colorTheme.light.palette['app-specific'].chart["1"],
                            "2": colorTheme.light.palette['app-specific'].chart["2"],
                            "3": colorTheme.light.palette['app-specific'].chart["3"],
                            "4": colorTheme.light.palette['app-specific'].chart["4"],
                            "5": colorTheme.light.palette['app-specific'].chart["5"],
                            "6": colorTheme.light.palette['app-specific'].chart["6"],
                            "7": colorTheme.light.palette['app-specific'].chart["7"],
                            "8": colorTheme.light.palette['app-specific'].chart["8"],
                        },
                        "data-grid": {
                            'loading-overlay-background': colorTheme.light.palette['app-specific']["data-grid"]['loading-overlay-background'],
                            'cell-border-color': colorTheme.light.palette['app-specific']["data-grid"]['cell-border-color'],
                            'selected-cell-background-color': colorTheme.light.palette['app-specific']["data-grid"]['selected-cell-background-color'],
                            'hovered-cell-background-color': colorTheme.light.palette['app-specific']["data-grid"]['hovered-cell-background-color'],
                            'grouped-row-cell-background-color': colorTheme.light.palette['app-specific']["data-grid"]['grouped-row-cell-background-color'],
                            'last-left-pinned-box-shadow-color': colorTheme.light.palette['app-specific']["data-grid"]['last-left-pinned-box-shadow-color'],
                            'first-right-pinned-box-shadow-color': colorTheme.light.palette['app-specific']["data-grid"]['first-right-pinned-box-shadow-color'],
                            'detailed-panel-box-shadow': colorTheme.light.palette['app-specific']["data-grid"]['detailed-panel-box-shadow'],
                            'switch-wrapper-box-shadow': colorTheme.light.palette['app-specific']["data-grid"]['switch-wrapper-box-shadow'],
                            'popover-shadow-color': colorTheme.light.palette['app-specific']["data-grid"]['popover-shadow-color'],
                        },
                        dashedBorder: (borderRadius, color) => `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='${borderRadius}' ry='${borderRadius}' stroke='${encodeURIComponent(color)}' stroke-width='4' stroke-dasharray='10' stroke-dashoffset='10' stroke-linecap='round'/%3e%3c/svg%3e")`,
                    },
                    FilledInput: {
                        bg: colorTheme.light.palette.background.paper,
                        hoverBg: colorTheme.light.palette.background.paper,
                    },
                    grey: {
                        '50': '#FCFDFD',
                        '100': '#F9FAFB',
                        '200': '#F4F6F8',
                        '300': '#DFE3E8',
                        '400': '#C4CDD5',
                        '500': '#919EAB',
                        '600': '#637381',
                        '700': '#454F5B',
                        '800': '#1C252E',
                        '900': '#141A21',
                        'A100': '#f5f5f5',
                        'A200': '#eeeeee',
                        'A400': '#bdbdbd',
                        'A700': '#616161',
                        '50Channel': '252 253 253',
                        '100Channel': '249 250 251',
                        '200Channel': '244 246 248',
                        '300Channel': '223 227 232',
                        '400Channel': '196 205 213',
                        '500Channel': '145 158 171',
                        '600Channel': '99 115 129',
                        '700Channel': '69 79 91',
                        '800Channel': '28 37 46',
                        '900Channel': '20 26 33',
                    },
                },
            },
            dark: {
                palette: {
                    primary: {
                        main: colorTheme.dark.palette.primary.main,
                        light: colorTheme.dark.palette.primary.light,
                    },
                    secondary: {
                        lighter: colorTheme.dark.palette.secondary.lighter,
                        light: colorTheme.dark.palette.secondary.light,
                        main: colorTheme.dark.palette.secondary.main,
                    },
                    tertiary: {
                        light: colorTheme.dark.palette.tertiary.light,
                        main: colorTheme.dark.palette.tertiary.main,
                    },
                    background: {
                        paper: colorTheme.dark.palette.background.paper,
                        default: colorTheme.dark.palette.background.default,
                        secondary: colorTheme.dark.palette.background.secondary,
                        tertiary: colorTheme.dark.palette.background.tertiary,
                    },
                    warning: {
                        light: colorTheme.dark.palette.warning.light,
                        main: colorTheme.dark.palette.warning.main,
                    },
                    error: {
                        light: colorTheme.dark.palette.error.light,
                        main: colorTheme.dark.palette.error.main,
                    },
                    success: {
                        light: colorTheme.dark.palette.success.light,
                        main: colorTheme.dark.palette.success.main,
                        dark: colorTheme.dark.palette.success.dark,
                    },
                    common: {
                        white: colorTheme.dark.palette.common.white,
                        black: colorTheme.dark.palette.common.black,
                    },
                    divider: colorTheme.dark.palette.divider,
                    text: {
                        primary: colorTheme.dark.palette.text.primary,
                        secondary: colorTheme.dark.palette.text.secondary,
                        tertiary: colorTheme.dark.palette.text.tertiary,
                        disabled: colorTheme.dark.palette.text.disabled,
                    },
                    'app-specific': {
                        shadow: 'rgba(0,0,0,0.2)',
                        'card-border-color': colorTheme.dark.palette['app-specific']["card-border-color"],
                        'scroll-bar': {
                            track: colorTheme.dark.palette['app-specific']['scroll-bar'].track,
                            thumb: colorTheme.dark.palette['app-specific']['scroll-bar'].thumb,
                        },
                        'chart': {
                            "1": colorTheme.dark.palette['app-specific'].chart["1"],
                            "2": colorTheme.dark.palette['app-specific'].chart["2"],
                            "3": colorTheme.dark.palette['app-specific'].chart["3"],
                            "4": colorTheme.dark.palette['app-specific'].chart["4"],
                            "5": colorTheme.dark.palette['app-specific'].chart["5"],
                            "6": colorTheme.dark.palette['app-specific'].chart["6"],
                            "7": colorTheme.dark.palette['app-specific'].chart["7"],
                            "8": colorTheme.dark.palette['app-specific'].chart["8"],
                        },
                        "data-grid": {
                            'loading-overlay-background': colorTheme.dark.palette['app-specific']["data-grid"]['loading-overlay-background'],
                            'cell-border-color': colorTheme.dark.palette['app-specific']["data-grid"]['cell-border-color'],
                            'selected-cell-background-color': colorTheme.dark.palette['app-specific']["data-grid"]['selected-cell-background-color'],
                            'hovered-cell-background-color': colorTheme.dark.palette['app-specific']["data-grid"]['hovered-cell-background-color'],
                            'grouped-row-cell-background-color': colorTheme.dark.palette['app-specific']["data-grid"]['grouped-row-cell-background-color'],
                            'last-left-pinned-box-shadow-color': colorTheme.dark.palette['app-specific']["data-grid"]['last-left-pinned-box-shadow-color'],
                            'first-right-pinned-box-shadow-color': colorTheme.dark.palette['app-specific']["data-grid"]['first-right-pinned-box-shadow-color'],
                            'detailed-panel-box-shadow': colorTheme.dark.palette['app-specific']["data-grid"]['detailed-panel-box-shadow'],
                            'switch-wrapper-box-shadow': colorTheme.dark.palette['app-specific']["data-grid"]['switch-wrapper-box-shadow'],
                            'popover-shadow-color': colorTheme.dark.palette['app-specific']["data-grid"]['popover-shadow-color'],
                        },
                        dashedBorder: (borderRadius, color) => `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='${borderRadius}' ry='${borderRadius}' stroke='${encodeURIComponent(color)}' stroke-width='4' stroke-dasharray='10' stroke-dashoffset='10' stroke-linecap='round'/%3e%3c/svg%3e")`,
                    },
                    FilledInput: {
                        bg: colorTheme.dark.palette.background.paper,
                        hoverBg: colorTheme.dark.palette.background.paper,
                    },
                    grey: {
                        '50': '#FCFDFD',
                        '100': '#F9FAFB',
                        '200': '#F4F6F8',
                        '300': '#DFE3E8',
                        '400': '#C4CDD5',
                        '500': '#919EAB',
                        '600': '#637381',
                        '700': '#454F5B',
                        '800': '#1C252E',
                        '900': '#141A21',
                        'A100': '#f5f5f5',
                        'A200': '#eeeeee',
                        'A400': '#bdbdbd',
                        'A700': '#616161',
                        '50Channel': '252 253 253',
                        '100Channel': '249 250 251',
                        '200Channel': '244 246 248',
                        '300Channel': '223 227 232',
                        '400Channel': '196 205 213',
                        '500Channel': '145 158 171',
                        '600Channel': '99 115 129',
                        '700Channel': '69 79 91',
                        '800Channel': '28 37 46',
                        '900Channel': '20 26 33',
                    },
                },
            },
        },
        'app-specific': {},
        typography: {
            fontFamily: `Open Sans, ${UIUtils.themeVar('fontFamily-fallback')}`,
        },
        shape: {
            borderRadius: 8,
        },
        components: {
            MuiTooltip: {
                defaultProps: {
                    arrow: true,
                },
                styleOverrides: {
                    tooltip: () => ({
                        padding: 10,
                        fontWeight: 600,
                    }),
                },
            },
            MuiPaper: {
                defaultProps: {
                    elevation: 5,
                },
                styleOverrides: {
                    root: ({theme}) => ({
                        [`${UIUtils.withThemePrefix()}shadows-5`]: 'none',
                        color: theme.vars.palette.text.primary,
                        backgroundImage: 'none',
                    }),

                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: () => ({
                        fontSize: 14,
                    }),
                },
            },
            MuiFormControlLabel: {
                styleOverrides: {
                    label: () => ({
                        fontSize: 14,
                    }),
                },
            },
            MuiInputBase: {
                styleOverrides: {
                    root: {
                        fontSize: 14,
                        [`&.${inputBaseClasses.disabled}`]: {
                            cursor: 'not-allowed',
                        },
                    },
                },
            },
            MuiListItem: {
                styleOverrides: {
                    root: ({theme}) => ({
                        borderRadius: theme.shape.borderRadius,
                    }),
                },
            },
            MuiMenuItem: {
                styleOverrides: {
                    root: ({theme}) => ({
                        paddingBlock: 12,
                        margin: '5px 10px',
                        backgroundColor: 'transparent',
                        borderRadius: theme.shape.borderRadius,
                        [`&.${menuItemClasses.selected}`]: {
                            fontWeight: 700,
                            color: theme.vars.palette.secondary.main,
                            backgroundColor: theme.vars.palette.secondary.lighter,
                            '&:hover': {
                                backgroundColor: theme.vars.palette.secondary.lighter,
                            }
                        },
                        '&:hover': {
                            backgroundColor: theme.vars.palette.secondary.lighter,
                        }
                    }),
                }
            },
            MuiSelect: {
                defaultProps: {},
                styleOverrides: {
                    select: ({theme}) => ({
                        [`&.${selectClasses.disabled}`]: {
                            cursor: 'not-allowed',
                            color: theme.vars.palette.text.disabled,
                        },
                        '&:focus': {
                            backgroundColor: 'transparent',
                        },
                    }),
                }
            },
            MuiCheckbox: {
                defaultProps: {
                    color: 'secondary',
                },
            },
            MuiRadio: {
                defaultProps: {
                    color: 'secondary',
                },
            },
            MuiButton: {
                defaultProps: {
                    disableRipple: true,
                    disableTouchRipple: true,
                    disableFocusRipple: true,
                    disableElevation: true,
                    type: 'button',
                    color: 'primary',
                    variant: 'contained',
                    size: 'small',
                },
                styleOverrides: {
                    root: ({ownerState, theme}) => ({
                        // paddingBlock: '0.5rem',
                        // paddingInline: '1rem',
                        // outline: 'none',
                        textTransform: 'none',
                        fontWeight: 700,
                        '.fill': {
                            fill: 'currentColor',
                        },
                        '.stroke': {
                            stroke: 'currentColor',
                        },
                        '&:hover': {
                            '.fill': {
                                fill: 'currentColor',
                            },
                            '.stroke': {
                                stroke: 'currentColor',
                            },
                        },
                        ...(ownerState.variant === 'contained' && {
                            border: '1px solid transparent',
                            ...(ownerState.color === 'primary' && {
                                color: theme.vars.palette.background.paper,
                                backgroundColor: theme.vars.palette.primary.main,
                                '&:hover': {
                                    color: theme.vars.palette.primary.main,
                                    backgroundColor: theme.vars.palette.primary.light,
                                    borderColor: theme.vars.palette.primary.main,
                                },
                            }),
                            ...(ownerState.color === 'secondary' && {
                                color: theme.vars.palette.background.paper,
                                backgroundColor: theme.vars.palette.secondary.main,
                                '&:hover': {
                                    color: theme.vars.palette.secondary.main,
                                    backgroundColor: theme.vars.palette.secondary.light,
                                    borderColor: theme.vars.palette.secondary.main,
                                },
                            }),
                            ...(ownerState.color === 'tertiary' && {
                                color: theme.vars.palette.background.paper,
                                backgroundColor: theme.vars.palette.tertiary.main,
                                '&:hover': {
                                    color: theme.vars.palette.tertiary.main,
                                    backgroundColor: theme.vars.palette.tertiary.light,
                                    borderColor: theme.vars.palette.tertiary.main,
                                },
                            }),
                            ...(ownerState.color === 'error' && {
                                color: theme.vars.palette.background.paper,
                                backgroundColor: theme.vars.palette.error.main,
                                '&:hover': {
                                    color: theme.vars.palette.error.main,
                                    backgroundColor: theme.vars.palette.error.light,
                                    borderColor: theme.vars.palette.error.main,
                                },
                            }),
                            [`&.${buttonClasses.disabled}`]: {
                                backgroundColor: theme.vars.palette.action.disabledBackground,
                                color: theme.vars.palette.action.disabled,
                                '&:hover': {
                                    backgroundColor: theme.vars.palette.action.disabledBackground,
                                    color: theme.vars.palette.action.disabled,
                                    borderColor: 'transparent',
                                },
                            },
                        }),
                        ...(ownerState.variant === 'outlined' && {
                            border: '1px solid currentColor',
                            ...(ownerState.color === 'primary' && {
                                color: theme.vars.palette.primary.main,
                                borderColor: theme.vars.palette.primary.main,
                                backgroundColor: 'transparent',
                                '.fill-inverse': {
                                    fill: theme.vars.palette.background.paper,
                                },
                                '&:hover': {
                                    color: theme.vars.palette.primary.main,
                                    borderColor: theme.vars.palette.primary.main,
                                    backgroundColor: theme.vars.palette.primary.light,
                                    '.fill-inverse': {
                                        fill: theme.vars.palette.primary.light,
                                    },
                                },
                            }),
                            ...(ownerState.color === 'secondary' && {
                                color: theme.vars.palette.secondary.main,
                                borderColor: theme.vars.palette.secondary.main,
                                backgroundColor: 'transparent',
                                '&:hover': {
                                    color: theme.vars.palette.secondary.main,
                                    borderColor: theme.vars.palette.secondary.main,
                                    backgroundColor: theme.vars.palette.secondary.light,
                                },
                            }),
                            ...(ownerState.color === 'tertiary' && {
                                color: theme.vars.palette.tertiary.main,
                                borderColor: theme.vars.palette.tertiary.main,
                                backgroundColor: 'transparent',
                                '&:hover': {
                                    color: theme.vars.palette.tertiary.main,
                                    borderColor: theme.vars.palette.tertiary.main,
                                    backgroundColor: theme.vars.palette.tertiary.light,
                                },
                            }),
                            ...(ownerState.color === 'error' && {
                                color: theme.vars.palette.error.main,
                                borderColor: theme.vars.palette.error.main,
                                backgroundColor: 'transparent',
                                '&:hover': {
                                    color: theme.vars.palette.error.main,
                                    borderColor: theme.vars.palette.error.main,
                                    backgroundColor: theme.vars.palette.error.light,
                                },
                            }),
                            ...(ownerState.disabled && {
                                color: theme.vars.palette.action.disabled,
                                backgroundColor: theme.vars.palette.action.disabledBackground,
                                borderColor: theme.vars.palette.action.disabled,
                                '&:hover': {
                                    color: theme.vars.palette.action.disabled,
                                    backgroundColor: theme.vars.palette.action.disabledBackground,
                                    borderColor: theme.vars.palette.action.disabled,
                                },
                            })
                        }),
                        ...(ownerState.variant === 'text' && {
                            border: 'none',
                            background: 'transparent !important',
                            ...(ownerState.color === 'primary' && {
                                color: theme.vars.palette.primary.main,
                                '.fill-inverse': {
                                    fill: theme.vars.palette.background.paper,
                                },
                                '&:hover': {
                                    color: theme.vars.palette.primary.main,
                                    '.fill-inverse': {
                                        fill: theme.vars.palette.primary.light,
                                    },
                                },
                            }),
                            ...(ownerState.color === 'secondary' && {
                                color: theme.vars.palette.secondary.main,
                                '&:hover': {
                                    color: theme.vars.palette.secondary.main,
                                },
                            }),
                            ...(ownerState.color === 'tertiary' && {
                                color: theme.vars.palette.tertiary.main,
                                '&:hover': {
                                    color: theme.vars.palette.tertiary.main,
                                },
                            }),
                            ...(ownerState.color === 'error' && {
                                color: theme.vars.palette.error.main,
                                '&:hover': {
                                    color: theme.vars.palette.error.main,
                                },
                            }),
                            ...(ownerState.disabled && {
                                color: theme.vars.palette.action.disabled,
                                '&:hover': {
                                    color: theme.vars.palette.action.disabled,
                                },
                            })
                        }),
                        ...(ownerState.variant === 'dialog' && {
                            border: 'none',
                            background: 'transparent',
                            paddingBlock: '1rem',
                            paddingInline: '3rem',
                            ...(ownerState.color === 'primary' && {
                                color: theme.vars.palette.primary.main,
                                '&:hover': {
                                    color: theme.vars.palette.background.paper,
                                    backgroundColor: theme.vars.palette.primary.main,
                                },
                            }),
                            ...(ownerState.color === 'secondary' && {
                                color: theme.vars.palette.secondary.main,
                                '&:hover': {
                                    color: theme.vars.palette.background.paper,
                                    backgroundColor: theme.vars.palette.secondary.main,
                                },
                            }),
                            ...(ownerState.color === 'tertiary' && {
                                color: theme.vars.palette.tertiary.main,
                                '&:hover': {
                                    color: theme.vars.palette.background.paper,
                                    backgroundColor: theme.vars.palette.tertiary.main,
                                },
                            }),
                            ...(ownerState.color === 'error' && {
                                color: theme.vars.palette.error.main,
                                '&:hover': {
                                    color: theme.vars.palette.background.paper,
                                    backgroundColor: theme.vars.palette.error.main,
                                },
                            }),
                            ...(ownerState.disabled && {
                                color: theme.vars.palette.action.disabled,
                                '&:hover': {
                                    color: theme.vars.palette.action.disabled,
                                    backgroundColor: theme.vars.palette.action.disabledBackground,
                                },
                            })
                        }),
                    }),
                }
            },
            MuiIconButton: {
                defaultProps: {
                    size: 'small',
                },
                styleOverrides: {
                    root: ({ownerState, theme}) => ({
                        padding: 5,
                        '.fill': {
                            fill: 'currentColor',
                        },
                        '.stroke': {
                            stroke: 'currentColor',
                        },
                        '&:hover': {
                            color: theme.vars.palette[ownerState.color as 'primary']?.main ?? "currentColor",
                            '.fill': {
                                fill: 'currentColor',
                            },
                            '.stroke': {
                                stroke: 'currentColor',
                            },
                        },
                        ...(ownerState.color?.startsWith('paper.') && {
                            backgroundColor: theme.vars.palette.background.paper,
                            color: theme.vars.palette[ownerState.color?.replace('paper.', '') as 'primary']?.main ?? 'currentColor',
                            '&:hover': {
                                color: theme.vars.palette[ownerState.color?.replace('paper.', '') as 'primary']?.main ?? 'currentColor',
                                backgroundColor: `rgba(${UIUtils.themeVar(`palette-${ownerState.color?.replace('paper.', '')}-mainChannel`)} / ${UIUtils.themeVar('palette-action-hoverOpacity')})`,
                                '.fill': {
                                    fill: 'currentColor',
                                },
                                '.stroke': {
                                    stroke: 'currentColor',
                                },
                            },
                        }),
                    }),
                }
            },
            MuiFormHelperText: {
                styleOverrides: {
                    root: {
                        [`&${formHelperTextClasses.error}`]: {
                            marginBlock: 10,
                        }
                    }
                }
            },
            MuiAutocomplete: {
                defaultProps: {
                    componentsProps: {
                        popupIndicator: {
                            color: "paper.secondary",
                        },
                        clearIndicator: {
                            color: "paper.secondary",
                        },
                        paper: {
                            elevation: 4,
                        },
                    },
                },
                styleOverrides: {
                    option: ({theme}) => ({
                        margin: 10,
                        borderRadius: 7,
                        [`&.Mui-selected , $[aria-selected="true"]`]: {
                            backgroundColor: theme.vars.palette.secondary.light,
                            '&:hover': {
                                backgroundColor: theme.vars.palette.secondary.lighter,
                            },
                        },
                        '&:hover': {
                            backgroundColor: theme.vars.palette.secondary.lighter,
                        },
                    }),
                    listbox: ({theme}) => ({
                        [`& .${autocompleteClasses.option}.Mui-selected , .${autocompleteClasses.option}[aria-selected="true"]`]: {
                            backgroundColor: theme.vars.palette.secondary.light,
                            '&:hover': {
                                backgroundColor: theme.vars.palette.secondary.lighter,
                            },
                        },

                    }),
                },
            },
            MuiDatePicker: {
                defaultProps: {
                    format: DateUtils.MUIFormat.datePicker,
                    timezone: DateUtils.localTimezone,
                    slotProps: {
                        inputAdornment: {
                            position: 'end',
                        },
                        actionBar: {
                            actions: ['today', 'clear', 'cancel', 'accept'],
                        },
                    },
                },
            },
            MuiTimePicker: {
                defaultProps: {
                    format: DateUtils.MUIFormat.timePicker,
                    timezone: DateUtils.localTimezone,
                    slotProps: {
                        inputAdornment: {
                            position: 'end',
                        },
                        actionBar: {
                            actions: ['today', 'clear', 'cancel', 'accept'],
                        },
                    },
                },
            },
            MuiDateTimePicker: {
                defaultProps: {
                    format: DateUtils.MUIFormat.dateTimePicker,
                    timezone: DateUtils.localTimezone,
                    slotProps: {
                        inputAdornment: {
                            position: 'end',
                        },
                        actionBar: {
                            actions: ['today', 'clear', 'cancel', 'accept'],
                        },
                    },
                },
            },
            MuiTabs: {
                defaultProps: {
                    value: false,
                },
                styleOverrides: {
                    root: ({theme}) => ({
                        ...(theme.palette.mode === 'light' && {
                            borderBottom: `1px solid rgba(0, 0, 0, 0.12)`,
                        }),
                        ...(theme.palette.mode === 'dark' && {
                            borderBottom: `1px solid rgba(255, 255, 255, 0.12)`,
                        }),
                    }),
                    indicator: ({theme}) => ({
                        backgroundColor: theme.vars.palette.secondary.main,
                    }),
                },
            },
            MuiTab: {
                defaultProps: {
                    disableRipple: true,
                    disableFocusRipple: true,
                    disableTouchRipple: true,
                },
                styleOverrides: {
                    root: ({theme}) => ({
                        textTransform: 'none',
                        minWidth: 72,
                        fontWeight: 400,
                        fontSize: 15,
                        transition: '0.3s font-weight cubic-bezier(0.4, 0, 0.2, 1)',
                        color: theme.vars.palette.text.tertiary,
                        '&:hover': {
                            color: theme.vars.palette.secondary.main,
                            opacity: 1,
                        },
                        '&:focus': {
                            color: theme.vars.palette.secondary.main,
                        },
                        [`&.${tabClasses.selected}`]: {
                            color: theme.vars.palette.secondary.main,
                            fontWeight: 700,
                        },
                    }),
                },
            },
            MuiSwitch: {
                defaultProps: {
                    disableRipple: true,
                    disableFocusRipple: true,
                    disableTouchRipple: true,
                },
                styleOverrides: {
                    root: ({
                        padding: 0,
                        margin: 0,
                    }),
                    switchBase: ({ownerState, theme}) => ({
                        padding: 1,
                        color: theme.vars.palette.background.paper,
                        "&:hover": {
                            backgroundColor: 'unset',
                        },
                        ...(ownerState.color === 'primary' && {
                            [`&.${switchClasses.checked}:not(.${switchClasses.disabled}) + .${switchClasses.track}`]: {
                                opacity: 1,
                                backgroundColor: 'transparent',
                                color: theme.vars.palette.primary.main,
                            },
                        }),
                        ...(ownerState.color === 'secondary' && {
                            [`&.${switchClasses.checked}:not(.${switchClasses.disabled}) + .${switchClasses.track}`]: {
                                opacity: 1,
                                color: theme.vars.palette.secondary.main,
                                backgroundColor: 'transparent',
                            },
                        }),
                        [`&.${switchClasses.disabled}`]: {
                            color: theme.vars.palette.action.disabled,
                            [`& + .${switchClasses.track}`]: {
                                backgroundColor: theme.vars.palette.action.disabledBackground,
                            },
                        },
                        [`&.${switchClasses.checked} .${switchClasses.thumb}`]: {
                            boxShadow: `inset 0 0 5px ${theme.vars.palette.background.paper} !important`,
                        },
                    }),
                    track: ({ownerState, theme}) => ({
                        border: `1px solid transparent`,
                        backgroundColor: theme.vars.palette.background.paper,
                        opacity: 1,
                        transition: theme.transitions.create(['background-color', 'border']),
                        ...(ownerState.color === 'primary' && {
                            borderColor: theme.vars.palette.primary.main,
                        }),
                        ...(ownerState.color === 'secondary' && {
                            borderColor: theme.vars.palette.secondary.main,
                        }),
                    }),
                    thumb: ({theme}) => ({
                        boxShadow: `inset 0 0 5px ${theme.vars.palette['app-specific']['data-grid']['switch-wrapper-box-shadow']}`,
                    }),
                },
            },
            MuiToggleButtonGroup: {
                defaultProps: {
                    color: 'secondary',
                    exclusive: true,
                },
                styleOverrides: {
                    grouped: ({theme}) => ({
                        margin: theme.spacing(1),
                    }),
                },
            },
            MuiToggleButton: {
                defaultProps: {
                    color: 'secondary',
                    size: 'small',
                },
                styleOverrides: {
                    root: ({ownerState, theme}) => ({
                        transition: theme.transitions.create(['color', 'background-color', 'border-color']),
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: 15,
                        border: '1px solid transparent !important',
                        borderRadius: `${theme.shape.borderRadius}px !important`,
                        marginInline: `${theme.spacing(0.5)} !important`,
                        paddingBlock: `${theme.spacing(0.5)} !important`,
                        ...(ownerState.color === 'primary' && {
                            borderColor: `${theme.vars.palette.primary.main} !important`,
                            backgroundColor: 'transparent',
                            color: theme.vars.palette.primary.main,
                            '&:hover': {
                                borderColor: `${theme.vars.palette.primary.main} !important`,
                                backgroundColor: theme.vars.palette.primary.light,
                                color: theme.vars.palette.primary.main,
                            },
                            [`&.${toggleButtonClasses.selected}`]: {
                                borderColor: `${theme.vars.palette.primary.main} !important`,
                                backgroundColor: theme.vars.palette.primary.main,
                                color: theme.vars.palette.background.paper,
                                '&:hover': {
                                    borderColor: `${theme.vars.palette.primary.main} !important`,
                                    backgroundColor: theme.vars.palette.primary.main,
                                    color: theme.vars.palette.background.paper,
                                },
                            },
                        }),
                        ...(ownerState.color === 'secondary' && {
                            borderColor: `${theme.vars.palette.secondary.main} !important`,
                            backgroundColor: 'transparent',
                            color: theme.vars.palette.secondary.main,
                            '&:hover': {
                                borderColor: `${theme.vars.palette.secondary.main} !important`,
                                backgroundColor: theme.vars.palette.secondary.light,
                                color: theme.vars.palette.secondary.main,
                            },
                            [`&.${toggleButtonClasses.selected}`]: {
                                borderColor: `${theme.vars.palette.secondary.main} !important`,
                                backgroundColor: theme.vars.palette.secondary.main,
                                color: theme.vars.palette.background.paper,
                                '&:hover': {
                                    borderColor: `${theme.vars.palette.secondary.main} !important`,
                                    backgroundColor: theme.vars.palette.secondary.main,
                                    color: theme.vars.palette.background.paper,
                                },
                            },
                        }),
                    }),
                    selected: ({ownerState, theme}) => ({
                        transition: theme.transitions.create(['color', 'background-color', 'border-color']),
                        textTransform: 'none',
                        ...(ownerState.color === 'primary' && {
                            borderColor: theme.vars.palette.primary.main,
                            backgroundColor: theme.vars.palette.primary.main,
                            color: theme.vars.palette.background.paper,
                        }),
                        ...(ownerState.color === 'secondary' && {
                            borderColor: theme.vars.palette.secondary.main,
                            backgroundColor: theme.vars.palette.secondary.main,
                            color: theme.vars.palette.background.paper,
                        }),
                    }),
                },
            },
            MuiSlider: {
                defaultProps: {
                    color: 'secondary',
                },
                styleOverrides: {
                    root: ({theme, ownerState}) => ({
                        ...(ownerState.color === 'secondary' && {
                            color: theme.vars.palette.secondary.main,
                        })
                    }),
                    thumb: ({theme}) => ({
                        backgroundColor: theme.vars.palette.background.paper,
                        boxShadow: `0px 7px 7px ${theme.vars.palette['app-specific'].shadow}`,
                    }),
                    rail: ({theme}) => ({
                        opacity: 1,
                        backgroundColor: theme.vars.palette.background.tertiary,
                    }),
                    mark: ({theme}) => ({
                        backgroundColor: theme.vars.palette.background.tertiary,
                    }),
                    markActive: () => ({
                        opacity: 1,
                        backgroundColor: 'currentColor',
                    }),

                },
            },
        },
    })
};

/**
 * The default theme of the application.
 */
const theme = (colorTheme: Partial<Record<SupportedColorScheme, { palette?: Palette }>>) => responsiveFontSizes(muiTheme(Utils.deepMerge(
    {},
    DefaultColorTheme,
    Utils.imputeColorTheme(colorTheme),
    {
        light: {
            palette: {
                divider: DefaultColorTheme.light.palette.divider,
                "app-specific": {
                    "scroll-bar": DefaultColorTheme.light.palette["app-specific"]["scroll-bar"],
                },
            },
        },
        dark: {
            palette: {
                divider: DefaultColorTheme.dark.palette.divider,
                "app-specific": {
                    "scroll-bar": DefaultColorTheme.dark.palette["app-specific"]["scroll-bar"],
                }
            },
        },
    },
    {...(EnvService.enableDemoNameMasking ? {...MaskedColorTheme} : {})}
)));

export default theme;

